/**
 * Bob Laadt Op — bob-story ("Van zonnepanelen naar thuisbatterijen").
 * Bron: _design/Bob-laadt-op-thomas/werkbestanden/paginas/over-bob.html r. 1890–1994 (maakSlider).
 *
 * Het schuiven doet de browser (scroll-snap). Dit script zet alleen de knoppen en de stippen
 * erbij, en leest de scrollpositie uit in plaats van een eigen teller bij te houden — daardoor
 * blijven swipen en klikken automatisch synchroon.
 *
 * Met kaarten (geen volle dia's) is de stap één kaart plus de tussenruimte, en of je aan het
 * eind bent volgt uit de scrollpositie en niet uit de index: met 2,5 kaart in beeld is de
 * laatste kaart nooit een scrollpositie.
 *
 * Theme editor: een kaart (block) kiezen schuift de track naar die kaart.
 * Custom element, zodat het na een re-render in de theme editor opnieuw werkt; alle listeners
 * hangen aan één AbortController en worden in disconnectedCallback opgeruimd.
 */
class BobStory extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.raf = 0;

    const root = this.querySelector('[data-slider]') || this;
    const track = /** @type {HTMLElement|null} */ (root.querySelector('[data-slider-track]'));
    if (!track) return;
    const dias = /** @type {HTMLElement[]} */ (Array.from(track.querySelectorAll('[data-slider-dia]')));
    const stippen = /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll('[data-slider-stip]')));
    const vorige = /** @type {HTMLButtonElement|null} */ (root.querySelector('[data-slider-vorige]'));
    const volgende = /** @type {HTMLButtonElement|null} */ (root.querySelector('[data-slider-volgende]'));
    if (!dias.length) return;

    /* offsetLeft-verschil pakt de kaartbreedte én de gap in één keer, zonder de CSS-waarden
       hier te herhalen. */
    const stap = () => (dias.length > 1 ? dias[1].offsetLeft - dias[0].offsetLeft : dias[0].offsetWidth);
    const huidige = () => {
      const st = stap();
      return st ? Math.round(track.scrollLeft / st) : 0;
    };
    /** @param {number} i */
    const naar = (i) =>
      track.scrollTo({
        left: Math.max(0, Math.min(i, dias.length - 1)) * stap(),
      });

    const sync = () => {
      const i = huidige();
      stippen.forEach((s, n) => {
        s.classList.toggle('is-actief', n === i);
        if (n === i) s.setAttribute('aria-current', 'true');
        else s.removeAttribute('aria-current');
      });
      const rest = track.scrollWidth - track.clientWidth;
      if (vorige) vorige.disabled = track.scrollLeft <= 2;
      if (volgende) volgende.disabled = track.scrollLeft >= rest - 2;
    };

    vorige?.addEventListener('click', () => naar(huidige() - 1), { signal });
    volgende?.addEventListener('click', () => naar(huidige() + 1), { signal });
    stippen.forEach((s) => s.addEventListener('click', () => naar(Number(s.dataset.dia)), { signal }));

    /* Slepen met de muis. Touch en trackpad scrollen zelf al, maar met een gewone muis kun je
       een overflow-track niet verplaatsen — dan lijkt de slider stuk. Touch laten we aan de
       browser (pointerType 'touch' slaan we over), anders zou hij dubbel bewegen. */
    /** @type {{x: number, links: number, actief: boolean} | null} */
    let sleep = null;
    track.addEventListener(
      'pointerdown',
      (e) => {
        if (e.pointerType === 'touch' || e.button !== 0) return;
        sleep = { x: e.clientX, links: track.scrollLeft, actief: false };
      },
      { signal }
    );
    track.addEventListener(
      'pointermove',
      (e) => {
        if (!sleep) return;
        const dx = e.clientX - sleep.x;
        /* Pas vanaf een paar pixels is het een sleep en geen klik. */
        if (!sleep.actief && Math.abs(dx) < 4) return;
        if (!sleep.actief) {
          sleep.actief = true;
          track.setPointerCapture(e.pointerId);
          track.style.scrollBehavior = 'auto';
          track.style.cursor = 'grabbing';
        }
        track.scrollLeft = sleep.links - dx;
        e.preventDefault();
      },
      { signal }
    );
    /** @param {PointerEvent} e */
    const stopSleep = (e) => {
      if (!sleep) return;
      const was = sleep.actief;
      sleep = null;
      track.style.scrollBehavior = '';
      track.style.cursor = '';
      if (was) {
        if (track.hasPointerCapture?.(e.pointerId)) track.releasePointerCapture(e.pointerId);
        /* Netjes op de dichtstbijzijnde kaart laten vallen. */
        naar(huidige());
        /* Een sleep mag geen klik worden op de kaart eronder. */
        track.addEventListener('click', (ev) => ev.preventDefault(), { capture: true, once: true, signal });
      }
    };
    track.addEventListener('pointerup', stopSleep, { signal });
    track.addEventListener('pointercancel', stopSleep, { signal });
    /* Vangnet naast de CSS-regel: geen native drag op foto's. */
    track.addEventListener('dragstart', (e) => e.preventDefault(), { signal });

    /* scroll vuurt tijdens het slepen tientallen keren; één frame per keer is genoeg om de
       stippen bij te werken. */
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(sync);
      },
      { passive: true, signal }
    );
    window.addEventListener('resize', sync, { signal });

    /* Theme editor: de gekozen kaart in beeld schuiven. */
    this.addEventListener(
      'shopify:block:select',
      (e) => {
        const dia = /** @type {HTMLElement} */ (e.target).closest?.('[data-slider-dia]');
        const i = dia ? dias.indexOf(/** @type {HTMLElement} */ (dia)) : -1;
        if (i > -1) naar(i);
      },
      { signal }
    );

    sync();
  }

  disconnectedCallback() {
    this.abort?.abort();
    cancelAnimationFrame(this.raf);
  }
}

if (!customElements.get('bob-story')) {
  customElements.define('bob-story', BobStory);
}
